import { useCallback, useEffect, useRef, useState } from 'react';

import type { AuthError } from '@/features/auth/shared/types/auth';
import { mapClerkError } from '@/features/auth/shared/utils/clerkErrorMapper';
import { createFinalizeNavigate } from '@/features/auth/shared/utils/createFinalizeNavigate';
import { createSignUp } from '@/features/auth/shared/utils/createSignUp';
import { useClerk, useSignUp } from '@clerk/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

interface AcceptInvitationFormValues {
  firstName: string;
  lastName: string;
  password: string;
}

export const useAcceptInvitation = () => {
  const { signUp } = useSignUp();
  const clerk = useClerk();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState<'loading' | 'form' | 'error' | 'migrated'>('loading');
  const [error, setError] = useState<AuthError | null>(null);
  const [loading, setLoading] = useState(false);
  const ticketValidatedRef = useRef(false);

  const ticket = searchParams.get('__clerk_ticket');
  const clerkStatus = searchParams.get('__clerk_status');
  const email = searchParams.get('email');

  useEffect(() => {
    if (!clerk.loaded || ticketValidatedRef.current) return;
    ticketValidatedRef.current = true;

    if (!ticket) {
      setStep('error');
      return;
    }

    if (clerkStatus === 'sign_in') {
      setStep('migrated');
      return;
    }

    setStep('form');
  }, [clerk.loaded, ticket, clerkStatus]);

  const handleSubmit = useCallback(
    async (data: AcceptInvitationFormValues) => {
      setLoading(true);
      setError(null);

      try {
        // Single create() call with ticket + all user fields. The ticket is consumed
        // exactly once — no prior processTicket call that would cause
        // "organization_invitation_already_accepted" on production.
        const { error: createError } = await createSignUp(signUp, {
          strategy: 'ticket',
          ticket: ticket ?? '',
          firstName: data.firstName,
          lastName: data.lastName,
          password: data.password
        });

        if (createError) {
          setError(mapClerkError(createError, t));
          return false;
        }

        if (signUp.status === 'complete') {
          await signUp.finalize({ navigate: createFinalizeNavigate(navigate) });
          toast.success(t('success.auth.acceptInvitation'));
          return true;
        }

        setError({ type: 'general', message: t('error.acceptInvitation.unexpectedStatus') });
        return false;
      } catch (err) {
        setError(mapClerkError(err, t));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [signUp, ticket, navigate, t]
  );

  return {
    step,
    error,
    loading,
    isLoaded: clerk.loaded,
    hasTicket: !!ticket,
    email,
    handleSubmit
  };
};
